import { useId, useState } from 'react'
import type { FamilyState, Member } from '@domain/state.ts'
import { adultsOf } from '@domain/state.ts'
import type { ChildSetup } from '@domain/seed.ts'
import type { MemberId } from '@domain/ids.ts'
import { es } from '../strings/es.ts'
import { QuitarMiembro } from './QuitarMiembro.tsx'

/**
 * Quién vive en esta casa: añadir y quitar después del primer día.
 *
 * Hasta ahora la gente de la casa se decidía en la pantalla de alta y no había
 * vuelta: llegaba un hijo, o se montaba la casa con un adulto y luego entraba la
 * madre, y la única salida era empezar de cero.
 *
 * El hijo nuevo llega con sus rutinas y sus misiones —las mismas que la semilla— y
 * entrando en los premios que ya hay, porque un hijo con la tienda vacía no es lo
 * que nadie espera al añadir a alguien de la familia.
 */

const ACCENTS = ['#1c6a73', '#9c6c08', '#7d3c98', '#1a5c3a', '#a33a2a', '#2a4a8a'] as const
const EMOJIS = ['🦊', '🦉', '🐢', '🦁', '🐬', '🦔'] as const

export function GenteSetting({
  state,
  onAddAdult,
  onAddChild,
  onArchiveMember,
}: {
  state: FamilyState
  onAddAdult: (name: string) => void
  onAddChild: (child: Omit<ChildSetup, 'id'>) => void
  onArchiveMember: (memberId: MemberId, reason: string) => void
}) {
  const adultos = adultsOf(state)
  const cuantosHijos = [...state.members.values()].filter((m) => m.role === 'child').length

  const [nombreAdulto, setNombreAdulto] = useState('')
  const [hijo, setHijo] = useState({ name: '', year: '', literal: false, glutenFree: false })
  const adultoId = useId()
  const hijoId = useId()
  const anoId = useId()
  const literalId = useId()
  const glutenId = useId()

  const hijoCompleto = hijo.name.trim().length > 0 && /^\d{4}$/.test(hijo.year)

  return (
    <section className="border-rule mt-10 border-t pt-8">
      <h2 className="mt-0 mb-6 text-xl font-extrabold">{es.config.gente}</h2>

      <h3 className="text-muted mt-0 mb-1 text-xs font-bold tracking-[0.1em] uppercase">
        {es.config.adultos}
      </h3>
      <p className="text-muted mt-0 mb-3 text-sm">{es.config.adultosHelp}</p>
      <ul className="mb-4 grid list-none gap-2 p-0">
        {adultos.map((adulto: Member) => (
          <li
            key={adulto.id}
            className="border-rule m-0 flex flex-wrap items-center gap-3 rounded-xl border p-3"
          >
            <span className="min-w-0 flex-1 font-bold">{adulto.name}</span>
            {adultos.length > 1 ? (
              <QuitarMiembro member={adulto} onArchive={onArchiveMember} />
            ) : null}
          </li>
        ))}
      </ul>

      <div className="mb-8 flex flex-wrap items-end gap-3">
        <label className="text-sm font-bold" htmlFor={adultoId}>
          <span className="mb-1 block">{es.config.nuevoAdulto}</span>
          <input
            id={adultoId}
            className="border-rule bg-surface rounded-lg border px-4"
            value={nombreAdulto}
            onChange={(e) => {
              setNombreAdulto(e.target.value)
            }}
          />
        </label>
        <button
          type="button"
          disabled={nombreAdulto.trim().length === 0}
          className="bg-teal rounded px-5 font-bold text-white disabled:opacity-40"
          onClick={() => {
            onAddAdult(nombreAdulto.trim())
            setNombreAdulto('')
          }}
        >
          {es.config.anadirAdulto}
        </button>
      </div>

      <h3 className="text-muted mt-0 mb-1 text-xs font-bold tracking-[0.1em] uppercase">
        {es.config.nuevoHijo}
      </h3>
      <p className="text-muted mt-0 mb-3 text-sm">{es.config.nuevoHijoHelp}</p>

      <label className="mb-1 block text-sm font-bold" htmlFor={hijoId}>
        {es.setup.childName(cuantosHijos + 1)}
      </label>
      <input
        id={hijoId}
        className="border-rule bg-surface mb-3 w-full rounded-lg border px-4"
        value={hijo.name}
        onChange={(e) => {
          setHijo({ ...hijo, name: e.target.value })
        }}
      />

      <label className="mb-1 block text-sm font-bold" htmlFor={anoId}>
        {es.setup.childYear}
      </label>
      <input
        id={anoId}
        className="border-rule bg-surface mb-3 w-full rounded-lg border px-4"
        inputMode="numeric"
        maxLength={4}
        value={hijo.year}
        onChange={(e) => {
          setHijo({ ...hijo, year: e.target.value.replace(/\D/g, '') })
        }}
      />

      <div className="mb-3 flex items-start gap-3">
        <input
          id={literalId}
          type="checkbox"
          className="mt-3 h-6 w-6 min-h-0 shrink-0"
          checked={hijo.literal}
          onChange={(e) => {
            setHijo({ ...hijo, literal: e.target.checked })
          }}
        />
        <label htmlFor={literalId}>
          <span className="block font-bold">{es.setup.literal}</span>
          <span className="text-muted block text-sm">{es.setup.literalHelp}</span>
        </label>
      </div>

      <div className="mb-4 flex items-start gap-3">
        <input
          id={glutenId}
          type="checkbox"
          className="mt-3 h-6 w-6 min-h-0 shrink-0"
          checked={hijo.glutenFree}
          onChange={(e) => {
            setHijo({ ...hijo, glutenFree: e.target.checked })
          }}
        />
        <label htmlFor={glutenId}>
          <span className="block font-bold">{es.setup.glutenFree}</span>
          <span className="text-muted block text-sm">{es.setup.glutenFreeHelp}</span>
        </label>
      </div>

      <button
        type="button"
        disabled={!hijoCompleto}
        className="bg-teal rounded px-5 font-bold text-white disabled:opacity-40"
        onClick={() => {
          onAddChild({
            name: hijo.name.trim(),
            birthYear: Number(hijo.year),
            accent: ACCENTS[cuantosHijos % ACCENTS.length] ?? ACCENTS[0],
            emoji: EMOJIS[cuantosHijos % EMOJIS.length] ?? EMOJIS[0],
            literalLanguage: hijo.literal,
            glutenFree: hijo.glutenFree,
          })
          setHijo({ name: '', year: '', literal: false, glutenFree: false })
        }}
      >
        {es.config.anadirHijo}
      </button>
    </section>
  )
}
